/**
 * @passport/auth-session — the single accessor for "is anyone logged in right now, and what's
 * their session," across every @passport/* package that owns its own authenticated session
 * (currently @passport/store and @passport/auth-core). See
 * Documentation/Employees/agentic-coding/docs/passport-shared-auth-accessor-design.md for the
 * full design history and rationale — this package's public surface intentionally matches that
 * document's proposed shape exactly.
 *
 * Dependency direction: this package never imports a store. Each store imports this package and
 * calls registerAuthSource() once, at its own module load time, right after its own Zustand
 * store is created. That is what avoids a circular workspace dependency the moment more than one
 * store depends on this package (see the design doc's "why registerAuthSource" section).
 */

/** A resting, already-authenticated session, normalized to one canonical shape regardless of
 *  which underlying store/login-flow produced it. */
export interface AuthSession {
  token: string;
  refreshToken: string | null;
  expiresAt: number | null;
  user: {
    id: string;
    email: string;
    name: string;
    roles: string[];
    permissions: string[];
    tenantID?: number;
    tenantSlug?: string;
    environment?: string;
  };
}

type AuthSourceGetter = () => AuthSession | null;

interface RegisteredAuthSource {
  name: string;
  getSession: AuthSourceGetter;
}

// Module-level registry — one array per loaded copy of this package. In this monorepo's real
// pnpm workspace setup, every consumer bundled into a given app resolves `@passport/auth-session`
// to the same physical `workspace:*` copy, so a single in-module array is the correct, simplest
// choice; it is what lets registerAuthSource() stay a plain function instead of needing a
// globalThis-backed registry.
const registeredSources: RegisteredAuthSource[] = [];

/**
 * Registers one store as a possible source of the current session. Each store calls this once,
 * at its own module load time, passing a function that reads its own Zustand store's current
 * state and returns either a live AuthSession or null (not yet authenticated). Calling this
 * again with a name that's already registered replaces that source in place — its original
 * registration-order position is preserved, so re-evaluating a module (e.g. hot reload) never
 * silently duplicates or reorders sources.
 */
export function registerAuthSource(name: string, getSession: AuthSourceGetter): void {
  const existingIndex = registeredSources.findIndex((source) => source.name === name);
  if (existingIndex >= 0) {
    registeredSources[existingIndex] = { name, getSession };
    return;
  }
  registeredSources.push({ name, getSession });
}

const BEFORE_AUTH_TOKEN_FETCH_EVENT = 'passport:before-auth-token-fetch';

export interface BeforeAuthTokenFetchDetail {
  session: AuthSession | null;
}

type BeforeAuthTokenFetchHandler = (detail: BeforeAuthTokenFetchDetail) => void;

/**
 * Registers a synchronous override/interception handler, fired at the start of every
 * fetchAuthToken() call before any registered source is consulted. Implemented as a typed
 * wrapper around a `CustomEvent('passport:before-auth-token-fetch', { detail })` dispatch on
 * `window`, where `detail` is a mutable object the handler can write an override session into
 * (`detail.session = {...}`) to short-circuit the normal source lookup — this is the same
 * mechanism Atlas Forms' Form-Actions Player was independently about to build for itself as a
 * bespoke `onBeforeReadingAuth` event; this package centralizes that instead of a second,
 * differently-named copy.
 *
 * Returns an unsubscribe function. Outside a browser environment (no `window`), registering is a
 * safe no-op that returns a no-op unsubscribe — matches fetchAuthToken()'s own guard below.
 */
export function onBeforeAuthTokenFetch(handler: BeforeAuthTokenFetchHandler): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const listener = (event: Event) => {
    handler((event as CustomEvent<BeforeAuthTokenFetchDetail>).detail);
  };
  window.addEventListener(BEFORE_AUTH_TOKEN_FETCH_EVENT, listener);
  return () => {
    window.removeEventListener(BEFORE_AUTH_TOKEN_FETCH_EVENT, listener);
  };
}

/**
 * The single exported accessor. Fires onBeforeAuthTokenFetch's interceptor event first (if
 * anything is listening); if a handler wrote a session onto the event detail, that session is
 * returned immediately and no registered source is consulted. Otherwise checks every registered
 * source in registration order, returning the first one with a live (authenticated, non-empty
 * token) session — mirrors `passport/apps/login/src/ReturnUrlRedirectHandler.tsx`'s own
 * `useAuthSources().find(...)`, just centralized. Returns null if nothing is authenticated
 * anywhere (including when this package is loaded but nothing has ever called
 * registerAuthSource()).
 *
 * Never throws: a registered source's getter that throws is logged and skipped, not propagated —
 * a caller (e.g. Atlas Forms' Form-Actions Player) that treats "no session" as "proceed without a
 * JWT" must never be blocked by one misbehaving source.
 */
export function fetchAuthToken(): AuthSession | null {
  if (typeof window !== 'undefined') {
    const detail: BeforeAuthTokenFetchDetail = { session: null };
    window.dispatchEvent(new CustomEvent(BEFORE_AUTH_TOKEN_FETCH_EVENT, { detail }));
    if (detail.session && detail.session.token) {
      return detail.session;
    }
  }

  for (const source of registeredSources) {
    let session: AuthSession | null;
    try {
      session = source.getSession();
    } catch (error) {
      console.error(
        `[@passport/auth-session] registered source "${source.name}" threw while reading its session; skipping it`,
        error
      );
      continue;
    }
    if (session && session.token) {
      return session;
    }
  }

  return null;
}

/**
 * Test-only: clears the in-module registered-source list. Not part of this package's public
 * contract — exists solely so this package's own unit tests can start from a clean registry
 * instead of accumulating sources registered by earlier tests run in the same process.
 * @internal
 */
export function __resetAuthSourcesForTesting(): void {
  registeredSources.length = 0;
}
